import { defineMessages } from "../define";

/** The app shell (navigation, breadcrumbs, branch switcher, sign-in, user info) and shared list pieces. */
export default defineMessages({
  en: {
    // sidebar navigation and breadcrumbs (orders, pizzas and branches use common.*)
    "nav.dashboard": "Dashboard",
    "nav.operator": "Operator",
    "nav.pizzaTypes": "Pizza types",
    "nav.billing": "Billing",
    "nav.users": "Users",
    "nav.cameras": "Cameras",
    "nav.todos": "To-dos",
    "nav.userInfo": "User info",
    "nav.landingPage": "Landing page",
    "nav.monitoring": "Monitoring",
    "nav.evaluationSettings": "Evaluation",
    "nav.catalog": "Catalog",
    "nav.section.main": "Main",
    "nav.section.platform": "Platform",
    "nav.section.organization": "Organization",
    "nav.section.devices": "Devices",
    "nav.section.content": "Content",
    "nav.section.developer": "Developer",
    // sidebar footer: versions of the admin (FE) and of the backend it talks to (BE)
    "shell.version.frontend": "Admin (frontend) version",
    "shell.version.backend": "Backend version {version}",
    "shell.version.backendUnknown": "Backend version unknown (an older backend, or not reachable)",
    // user menu (nav-user)
    "nav.user.account": "Account",
    "nav.user.notifications": "Notifications",
    "nav.user.signOut": "Sign out",
    // breadcrumbs (App)
    "shell.breadcrumb.order": "Order {id}",
    "shell.breadcrumb.pizza": "Pizza {id}",
    // screen-reader labels of the UI primitives (sidebar, breadcrumbs)
    "shell.toggleSidebar": "Toggle sidebar",
    "shell.breadcrumbLabel": "Breadcrumb",
    "shell.more": "More",
    // branch switcher
    "shell.branchSwitcher.allBranches": "All branches",
    "shell.branchSwitcher.noRestriction": "No branch filter",
    "shell.branchSwitcher.selectedBranch": "Selected branch",
    "shell.branchSwitcher.selectedBranches": "Selected branches",
    "shell.branchSwitcher.signInRequired": "Please sign in",
    "shell.branchSwitcher.noBranchSelected": "No branch selected",
    "shell.branchSwitcher.fetching": "Fetching branches",
    "shell.branchSwitcher.assignedBranch": "Assigned branch",
    "shell.branchSwitcher.loadingBranch": "Loading branch…",
    "shell.branchSwitcher.branchId": "ID: {id}",
    "shell.branchSwitcher.noBranches": "No branches",
    "shell.branchSwitcher.noneAvailable": "None available",
    "shell.branchSwitcher.listLabel": "Branches ({count})",
    "shell.branchSwitcher.listLabelFiltered": "Branches ({shown} of {total})",
    "shell.branchSwitcher.searchPlaceholder": "Search branches…",
    "shell.branchSwitcher.noMatch": "No branch found",
    "shell.branchSwitcher.showAll": "All",
    "shell.branchSwitcher.selectAll": "Select all",
    // operator page
    "shell.operator.noPizza": "No evaluated pizza for the selected branch yet.",
    // sign-in form
    "login.form.title": "Sign in",
    "login.form.description": "Enter your email and password to sign in.",
    "login.form.email": "Email",
    "login.form.password": "Password",
    "login.form.submit": "Sign in",
    "login.form.submitting": "Signing in…",
    "login.form.failed": "Sign-in failed. Check your email and password and try again.",
    "login.form.useGoogle": "Developer accounts sign in with Google.",
    "login.form.or": "or",
    // "Sign in with Google" for the company Workspace (the button text itself comes from Google)
    "login.google.hint": "For {domain} accounts",
    "login.google.wrongDomain": "Only {domain} accounts can sign in with Google.",
    "login.google.disabled": "This account is deactivated.",
    "login.google.unavailable": "Google sign-in is temporarily unavailable. Try again later or sign in with your password.",
    "login.google.failed": "Google sign-in failed. Try again.",
    "login.google.loadFailed": "Google sign-in could not be loaded.",
    // user info page (/user-info)
    "userInfo.signInRequired": "Sign in to see your role information.",
    "userInfo.loadingRoles": "Loading roles…",
    "userInfo.user.title": "User information",
    "userInfo.user.email": "Email",
    "userInfo.user.userId": "User ID",
    "userInfo.user.roleId": "Role ID",
    "userInfo.user.branchId": "Branch ID",
    "userInfo.role.title": "Current role",
    "userInfo.role.role": "Role",
    "userInfo.role.enabled": "Enabled",
    "userInfo.role.permissions": "Permissions",
    "userInfo.classification.title": "Role classification",
    "userInfo.classification.admin": "Admin",
    "userInfo.classification.developer": "Developer",
    "userInfo.classification.branchOwner": "Branch owner",
    "userInfo.classification.operator": "Operator",
    "userInfo.roles.title": "All available roles",
    "userInfo.roles.permissions": "Permissions: {permissions}",
    // list pagination (the unit after the total: unit="pizzas" / "orders")
    "list.pagination.rowsPerPage": "Rows per page",
    "list.pagination.unitPizzas.one": "pizza",
    "list.pagination.unitPizzas.few": "pizzas",
    "list.pagination.unitPizzas.other": "pizzas",
    "list.pagination.unitOrders.one": "order",
    "list.pagination.unitOrders.few": "orders",
    "list.pagination.unitOrders.other": "orders",
    // table filters dialog (defaults)
    "list.filtersDialog.title": "Table filters",
    "list.filtersDialog.description": "Set the filters for the table data.",
    // check strip (verdict checks as icons)
    "list.checkStrip.label": "Checks",
    "list.checkStrip.photo": "Photo quality",
    "list.checkStrip.base": "Ingredients",
    "list.checkStrip.extras": "Extras",
    "list.checkStrip.removals": "Removals",
    "list.checkStrip.burnt": "Burnt",
    "list.checkStrip.raw": "Uncooked",
    "list.checkStrip.saver": "Pizza saver",
    "list.checkStrip.notEvaluated": "not evaluated",
    // multi-select (defaults)
    "list.multiSelect.placeholder": "Select…",
    "list.multiSelect.searchPlaceholder": "Search…",
    "list.multiSelect.selectAll": "Select all",
    "list.multiSelect.noOptions": "No options available",
    "list.multiSelect.noResults": "No matching options",
    "list.multiSelect.remove": "Remove {label}",
    "list.multiSelect.selected.one": "{count} item selected",
    "list.multiSelect.selected.few": "{count} items selected",
    "list.multiSelect.selected.other": "{count} items selected",
    "list.multiSelect.countFiltered": "({shown} of {total})",
  },
  cs: {
    // sidebar navigation and breadcrumbs (orders, pizzas and branches use common.*)
    "nav.dashboard": "Dashboard",
    "nav.operator": "Operátor",
    "nav.pizzaTypes": "Typy pizz",
    "nav.billing": "Fakturace",
    "nav.users": "Uživatelé",
    "nav.cameras": "Kamery",
    "nav.todos": "Úkoly",
    "nav.userInfo": "Informace o uživateli",
    "nav.landingPage": "Úvodní stránka",
    "nav.monitoring": "Monitoring",
    "nav.evaluationSettings": "Vyhodnocení",
    "nav.catalog": "Katalog",
    "nav.section.main": "Hlavní",
    "nav.section.platform": "Platforma",
    "nav.section.organization": "Organizace",
    "nav.section.devices": "Zařízení",
    "nav.section.content": "Obsah",
    "nav.section.developer": "Vývoj",
    "shell.version.frontend": "Verze adminu (frontendu)",
    "shell.version.backend": "Verze backendu {version}",
    "shell.version.backendUnknown": "Verze backendu není známa (starší backend, nebo nedostupný)",
    // user menu (nav-user)
    "nav.user.account": "Účet",
    "nav.user.notifications": "Oznámení",
    "nav.user.signOut": "Odhlásit se",
    // breadcrumbs (App)
    "shell.breadcrumb.order": "Objednávka {id}",
    "shell.breadcrumb.pizza": "Pizza {id}",
    // screen-reader labels of the UI primitives (sidebar, breadcrumbs)
    "shell.toggleSidebar": "Zobrazit / skrýt boční panel",
    "shell.breadcrumbLabel": "Drobečková navigace",
    "shell.more": "Další",
    // branch switcher
    "shell.branchSwitcher.allBranches": "Všechny pobočky",
    "shell.branchSwitcher.noRestriction": "Bez omezení",
    "shell.branchSwitcher.selectedBranch": "Vybraná pobočka",
    "shell.branchSwitcher.selectedBranches": "Vybrané pobočky",
    "shell.branchSwitcher.signInRequired": "Přihlaste se",
    "shell.branchSwitcher.noBranchSelected": "Není vybrána žádná pobočka",
    "shell.branchSwitcher.fetching": "Načítají se pobočky",
    "shell.branchSwitcher.assignedBranch": "Přiřazená pobočka",
    "shell.branchSwitcher.loadingBranch": "Načítání pobočky…",
    "shell.branchSwitcher.branchId": "ID: {id}",
    "shell.branchSwitcher.noBranches": "Žádné pobočky",
    "shell.branchSwitcher.noneAvailable": "Žádná není k dispozici",
    "shell.branchSwitcher.listLabel": "Pobočky ({count})",
    "shell.branchSwitcher.listLabelFiltered": "Pobočky ({shown} z {total})",
    "shell.branchSwitcher.searchPlaceholder": "Hledat pobočku…",
    "shell.branchSwitcher.noMatch": "Žádná pobočka nenalezena",
    "shell.branchSwitcher.showAll": "Všechny",
    "shell.branchSwitcher.selectAll": "Vybrat vše",
    // operator page
    "shell.operator.noPizza": "Pro vybranou pobočku zatím není žádná vyhodnocená pizza.",
    // sign-in form
    "login.form.title": "Přihlášení",
    "login.form.description": "Přihlaste se svým e-mailem a heslem.",
    "login.form.email": "E-mail",
    "login.form.password": "Heslo",
    "login.form.submit": "Přihlásit se",
    "login.form.submitting": "Přihlašování…",
    "login.form.failed": "Přihlášení se nezdařilo. Zkontrolujte e-mail a heslo a zkuste to znovu.",
    "login.form.useGoogle": "Vývojářské účty se přihlašují přes Google.",
    "login.form.or": "nebo",
    "login.google.hint": "Pro účty {domain}",
    "login.google.wrongDomain": "Přes Google se mohou přihlásit jen účty {domain}.",
    "login.google.disabled": "Tento účet je deaktivovaný.",
    "login.google.unavailable": "Přihlášení přes Google je dočasně nedostupné. Zkuste to později nebo se přihlaste heslem.",
    "login.google.failed": "Přihlášení přes Google se nezdařilo. Zkuste to znovu.",
    "login.google.loadFailed": "Přihlášení přes Google se nepodařilo načíst.",
    // user info page (/user-info)
    "userInfo.signInRequired": "Pro zobrazení informací o roli se přihlaste.",
    "userInfo.loadingRoles": "Načítání rolí…",
    "userInfo.user.title": "Informace o uživateli",
    "userInfo.user.email": "E-mail",
    "userInfo.user.userId": "ID uživatele",
    "userInfo.user.roleId": "ID role",
    "userInfo.user.branchId": "ID pobočky",
    "userInfo.role.title": "Aktuální role",
    "userInfo.role.role": "Role",
    "userInfo.role.enabled": "Aktivní",
    "userInfo.role.permissions": "Oprávnění",
    "userInfo.classification.title": "Zařazení role",
    "userInfo.classification.admin": "Administrátor",
    "userInfo.classification.developer": "Vývojář",
    "userInfo.classification.branchOwner": "Vlastník pobočky",
    "userInfo.classification.operator": "Operátor",
    "userInfo.roles.title": "Všechny dostupné role",
    "userInfo.roles.permissions": "Oprávnění: {permissions}",
    // list pagination (the unit after the total: unit="pizzas" / "orders")
    "list.pagination.rowsPerPage": "Řádků na stránku",
    "list.pagination.unitPizzas.one": "pizza",
    "list.pagination.unitPizzas.few": "pizzy",
    "list.pagination.unitPizzas.other": "pizz",
    "list.pagination.unitOrders.one": "objednávka",
    "list.pagination.unitOrders.few": "objednávky",
    "list.pagination.unitOrders.other": "objednávek",
    // table filters dialog (defaults)
    "list.filtersDialog.title": "Filtry tabulky",
    "list.filtersDialog.description": "Nastavte filtry pro data v tabulce.",
    // check strip (verdict checks as icons)
    "list.checkStrip.label": "Kontroly",
    "list.checkStrip.photo": "Kvalita fotky",
    "list.checkStrip.base": "Suroviny",
    "list.checkStrip.extras": "Přídavky",
    "list.checkStrip.removals": "Odebrané suroviny",
    "list.checkStrip.burnt": "Spálená",
    "list.checkStrip.raw": "Nedopečená",
    "list.checkStrip.saver": "Pizza saver",
    "list.checkStrip.notEvaluated": "nevyhodnoceno",
    // multi-select (defaults)
    "list.multiSelect.placeholder": "Vyberte…",
    "list.multiSelect.searchPlaceholder": "Hledat…",
    "list.multiSelect.selectAll": "Vybrat vše",
    "list.multiSelect.noOptions": "Žádné možnosti",
    "list.multiSelect.noResults": "Nic nenalezeno",
    "list.multiSelect.remove": "Odebrat {label}",
    "list.multiSelect.selected.one": "{count} vybraná položka",
    "list.multiSelect.selected.few": "{count} vybrané položky",
    "list.multiSelect.selected.other": "{count} vybraných položek",
    "list.multiSelect.countFiltered": "({shown} z {total})",
  },
});
